import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import "../styles/process-studio.css";
import { getProcessInformation } from "../services/processService";
import { useAppContext } from "../context/AppContext";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import ProcessUserStoriesPage from "./ProcessUserStoriesPage";
import ProcessUseCasePage from "./ProcessUseCasePage";
import ProcessPrototypePage from "./ProcessPrototypePage";

const formatProcessOptionLabel = (process) => {
  const processName =
    process?.process_name || process?.name || "Unnamed process";
  const processVersion = process?.process_version ?? process?.version ?? 0;
  const companyName = process?.company?.name || "";

  return `${processName} (V${processVersion})| ${companyName}`;
};

const icon = (path) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d={path}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Each tab keeps its own section (and URL), so links and bookmarks still work.
const TABS = [
  {
    id: "stories",
    section: "process-user-stories",
    label: "User Stories",
    hint: "Epic, stories and model issues",
    icon: icon("M8 6h12M8 12h12M8 18h8M4 6h.01M4 12h.01M4 18h.01"),
    Component: ProcessUserStoriesPage,
  },
  {
    id: "uml",
    section: "process-usecase",
    label: "Process UML",
    hint: "Use-case and activity diagrams",
    icon: icon(
      "M4 4h6v5H4zM14 15h6v5h-6zM4 15h6v5H4zM7 9v6M10 17.5h4M17 15V6.5h-7",
    ),
    Component: ProcessUseCasePage,
  },
  {
    id: "prototype",
    section: "process-prototype",
    label: "Prototype",
    hint: "Runnable digital twin of the process",
    icon: icon("M8 9l-4 3 4 3M16 9l4 3-4 3M13.5 6l-3 12"),
    Component: ProcessPrototypePage,
  },
];

/**
 * Process Studio Page
 * One place for everything generated from a process: user stories, UML
 * diagrams and the digital-twin prototype. The process is picked once and
 * shared by every tab. A tab is mounted the first time it is opened and then
 * kept (hidden) so a long AI run carries on while the user looks at another
 * tab; the tab bar shows which tabs are still working.
 */
const ProcessStudioPage = ({ tab }) => {
  const { setActiveSection } = useAppContext();
  const activeTab = TABS.some((t) => t.id === tab) ? tab : TABS[0].id;

  const [labelToId, setLabelToId] = React.useState(new Map());
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);
  const [visited, setVisited] = React.useState(() => new Set([activeTab]));
  const [busy, setBusy] = React.useState({});

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getProcessInformation();
        if (!mounted) return;
        const map = new Map();
        (Array.isArray(list) ? list : []).forEach((p) => {
          map.set(formatProcessOptionLabel(p), p.process_id || p.id);
        });
        setLabelToId(map);
      } catch (e) {
        if (mounted) setLabelToId(new Map());
        console.error("Failed to load processes for the process studio", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    setVisited((v) => (v.has(activeTab) ? v : new Set(v).add(activeTab)));
  }, [activeTab]);

  // One stable callback per tab, so the tabs' effects don't re-run.
  const busyHandlers = React.useMemo(
    () =>
      Object.fromEntries(
        TABS.map((t) => [
          t.id,
          (isBusy) =>
            setBusy((b) => (b[t.id] === isBusy ? b : { ...b, [t.id]: isBusy })),
        ]),
      ),
    [],
  );

  const processOptions = React.useMemo(
    () =>
      Array.from(labelToId.entries()).map(([label, id]) => ({
        label,
        value: String(id),
      })),
    [labelToId],
  );

  const onProcessChange = (label) => {
    if (!label) {
      setSelectedLabel("");
      setSelectedId(null);
      return;
    }
    setSelectedLabel(label);
    setSelectedId(labelToId.get(String(label).trim()) ?? null);
  };

  const anyBusy = TABS.some((t) => busy[t.id]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Process Studio</h2>
            <p className="page-subtitle">
              Turn a process into user stories, UML diagrams and a runnable
              prototype
            </p>
          </div>
        </div>
      </div>

      <div className="page-content whatif">
        <div className="role-card ps-process-card">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="process-studio-search-select">Process</label>
            <SearchableDropdown
              id="process-studio-search-select"
              className="input searchable-combobox"
              value={selectedLabel}
              options={processOptions}
              placeholder="Search process by name/company"
              clearOnFocus
              onChange={(label, option) =>
                onProcessChange(option?.label ?? label)
              }
            />
          </div>
          <p className="ps-process-hint">
            {anyBusy ?
              "Changing the process stops any generation that is still running."
            : "Pick a process once; every tab below works on it."}
          </p>
        </div>

        <div className="ps-tabs" role="tablist" aria-label="Process studio">
          {TABS.map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`ps-tab-${t.id}`}
                aria-selected={active}
                aria-controls={`ps-panel-${t.id}`}
                className={`ps-tab${active ? " active" : ""}`}
                onClick={() => setActiveSection(t.section)}
              >
                <span className="ps-tab-icon">{t.icon}</span>
                <span className="ps-tab-text">
                  <span className="ps-tab-label">
                    {t.label}
                    {busy[t.id] && (
                      <span
                        className="ps-tab-busy"
                        title="Still working"
                        aria-label="Still working"
                      />
                    )}
                  </span>
                  <span className="ps-tab-hint">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {!selectedId && (
          <div className="role-card ps-empty">
            <span className="ps-empty-icon">
              {TABS.find((t) => t.id === activeTab).icon}
            </span>
            <div className="ps-empty-title">Select a process to begin</div>
            <div className="ps-empty-text">
              Choose a process above to work with its{" "}
              {TABS.find((t) => t.id === activeTab).label.toLowerCase()}.
            </div>
          </div>
        )}

        {TABS.filter((t) => visited.has(t.id)).map(({ id, Component }) => (
          <div
            key={id}
            role="tabpanel"
            id={`ps-panel-${id}`}
            aria-labelledby={`ps-tab-${id}`}
            hidden={!selectedId || id !== activeTab}
          >
            <Component
              processId={selectedId}
              processLabel={selectedLabel}
              onBusyChange={busyHandlers[id]}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProcessStudioPage;
